import React, { useRef, useLayoutEffect, useState } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { motion } from "framer-motion";
import { springVariants, springViewport } from '../utils/framerVariants';

import { register } from 'swiper/element/bundle';
register();

const Carousels = ({ data }) => {
  if (!data) return null;

  const isSmallCarousel = data.LayoutCarousel?.startsWith("Normal");
  const [totalSlides, setTotalSlides] = useState(0);
  const [activeSlide, setActiveSlide] = useState(1);
  const swiperElRef = useRef(null);

  useLayoutEffect(() => {
    let swiperInstance = null;

    const initSwiper = async () => {
      try {
        await customElements.whenDefined('swiper-container');
        
        if (swiperElRef.current?.swiper) {
          swiperInstance = swiperElRef.current.swiper;
          setTotalSlides(swiperInstance.slides?.length || 0);

          swiperInstance.on('slideChange', () => {
            setActiveSlide((swiperInstance?.activeIndex ?? 0) + 1);
          });
        }
      } catch (err) {
        console.warn('Swiper initialization error:', err);
      }
    };

    initSwiper();

    return () => {
      swiperInstance?.off?.('slideChange');
    };
  }, []);

  const handleSlideNext = () => {
    swiperElRef.current?.swiper?.slideNext?.();
  };

  const handleSlidePrevious = () => {
    swiperElRef.current?.swiper?.slidePrev?.();
  };

  const renderCaption = (entry) => {
    if (!entry) return null;

    if (entry.captionText) {
      return (
        <div 
          className="caption-rich-text" 
          dangerouslySetInnerHTML={{ __html: entry.captionText }} 
        />
      );
    }

    if (entry.Media?.caption) {
      return (
        <p className="inline-flex mb-0 text-text-sm tracking-text-sm leading-text-sm text-black/50 max-w-2xl">
          {entry.Media.caption}
        </p>
      );
    }

    return null;
  };

  const renderMedia = (entry) => {
    if (!entry?.Media) return null;

    const isImage = entry.Media.mime?.startsWith('image/');
    const isVideo = entry.Media.mime?.startsWith('video/');

    if (isImage) {
      const image = getImage(entry.Media.file);
      if (!image) return null;

      return (
        <GatsbyImage
          className="h-full w-full bg-black"
          image={image}
          alt={entry.Media.alternativeText || "Image"}
        />
      );
    }

    if (isVideo && entry.Media.file?.publicURL) {
      return (
        <video 
          className="w-full h-full object-cover"
          src={entry.Media.file.publicURL}
          alt={entry.Media.alternativeText || "Video"}
          controls
        />
      );
    }

    return null;
  };

  const carouselEntries = Array.isArray(data?.CarouselEntry) ? data.CarouselEntry : [];
  if (carouselEntries.length === 0) return null;

  const Wrapper = motion?.div ?? 'div';
  const wrapperProps = motion?.div ? {
    variants: springVariants,
    initial: "invisible",
    whileInView: "visible",
    viewport: springViewport
  } : {};

  return (
    <article className="container mx-auto max-w-none mb-16 md:mb-24 lg:mb-32 xl:mb-48 pl-4 md:pl-8">
      <Wrapper {...wrapperProps}>
        <div className="relative study-swiper">
          <swiper-container
            ref={swiperElRef}
            slides-per-view={isSmallCarousel ? 1.33 : 1.0}
            space-between={isSmallCarousel ? 0 : 0}
            centered-slides={isSmallCarousel ? false : true}
            lazyPreloadPrevNext={1}
            keyboard={true}
            speed="240"
          >
            {carouselEntries.map((entry, i) => {
              if (!entry) return null;

              const media = renderMedia(entry);
              if (!media && !entry.captionText && !entry.Media?.caption) return null;

              return (
                <swiper-slide key={i}>
                  <div className={`
                    ${isSmallCarousel 
                      ? 'md:h-[80vh] w-full pr-4 md:pr-8' 
                      : 'bg-white container mx-auto max-w-6xl lg:max-w-7xl 2xl:max-w-8xl px-4 md:px-24 lg:px-24 xl:px-24'
                    }
                  `}>
                    {media}
                  </div>
                  
                  <div className="mt-2">
                    {renderCaption(entry)}
                  </div>
                </swiper-slide>
              );
            })}
          </swiper-container>
          
          {carouselEntries.length > 1 && (
            <>
              <div 
                onClick={handleSlidePrevious} 
                className={`arrows cursor-pointer absolute z-20 top-[50%] left-5 -mt-10 lg:-mt-12 ${
                  activeSlide === 1 ? 'pointer-events-none opacity-0' : ''
                }`}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-12 rotate-180"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </div>
              <div 
                onClick={handleSlideNext} 
                className={`arrows cursor-pointer absolute z-20 top-[50%] right-10 -mt-10 lg:-mt-12 ${
                  activeSlide === totalSlides ? 'pointer-events-none opacity-0' : ''
                }`}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-12"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </div>
            </>
          )}
        </div>
      </Wrapper>
    </article>
  );
};

export default Carousels;